/**
 * Focus a target the palette navigated to but doesn't own the DOM node for
 * (an inline card's own input, e.g. `EmailInviteForm`'s email field or
 * `NewWorkspaceCard`'s name field — the product decision for both is
 * "navigate + focus the existing inline input", not a new dialog).
 *
 * The route's component mounts asynchronously (`lazyRouteComponent`), so the
 * target may not exist in the DOM the instant `navigate()`'s promise
 * resolves. There's no existing app-wide "run after route settles" hook to
 * reuse (checked `lib/`, `features/*`), so this polls on
 * `requestAnimationFrame` for the element carrying
 * `data-command-focus-target="<target>"` and gives up after ~1s.
 *
 * // ponytail: rAF-polling ceiling of ~1s / 60 frames. If a target route ever
 * // needs longer to mount its target, upgrade to awaiting the route's own
 * // loader/component-ready signal instead of raising this ceiling.
 */
export function focusCommandTarget(target: string): void {
  const MAX_FRAMES = 60;
  let frame = 0;

  function attempt() {
    const el = document.querySelector<HTMLElement>(`[data-command-focus-target="${target}"]`);
    if (el) {
      // `preventScroll` because focusing is not allowed to move the viewport
      // here — `scrollToTarget` below owns that, and it is deliberately
      // narrower than what the browser would do on its own. See its docblock.
      el.focus({ preventScroll: true });
      scrollToTarget(el);
      return;
    }
    frame += 1;
    if (frame < MAX_FRAMES) requestAnimationFrame(attempt);
  }

  requestAnimationFrame(attempt);
}

/**
 * The nearest ancestor that is genuinely scrollable — `overflow-y` of `auto`
 * or `scroll` AND currently overflowing.
 *
 * **`overflow: hidden` deliberately does not qualify, and that is the whole
 * point of this function.** A `hidden` box still has a scroll box: it is
 * scrollable by script even though it shows no scrollbar and refuses the
 * user's wheel. The app shell has two of them stacked above every route —
 * the root flex row and `<main>` (`components/organisms/AppShell.tsx`) — so
 * anything that walks the full ancestor chain can scroll the sidebar and
 * topbar out of view and leave the user with no way to bring them back.
 */
function findScrollContainer(el: HTMLElement): HTMLElement | null {
  for (let node = el.parentElement; node; node = node.parentElement) {
    const { overflowY } = getComputedStyle(node);
    const scrollable = overflowY === 'auto' || overflowY === 'scroll';
    if (scrollable && node.scrollHeight > node.clientHeight) return node;
  }
  return null;
}

/**
 * Centre `el` in its own scroll container, scrolling nothing else.
 *
 * This replaces `el.scrollIntoView({ block: 'center' })`, which scrolls
 * *every* scrollable ancestor rather than just the one that owns the page's
 * scrollbar — including the shell's two `overflow: hidden` boxes, which is
 * how "focus the invite field" ended up shifting the entire UI upward.
 * Scrolling exactly one container is not an optimisation of that call; it is
 * the behaviour difference being fixed.
 *
 * No scrollable ancestor means the target is already on screen, so there is
 * nothing to do — falling back to `scrollIntoView` would reintroduce the bug.
 */
function scrollToTarget(el: HTMLElement): void {
  const container = findScrollContainer(el);
  if (!container) return;

  const elRect = el.getBoundingClientRect();
  const containerRect = container.getBoundingClientRect();
  const offsetWithin = elRect.top - containerRect.top;
  const centred = offsetWithin - (container.clientHeight - elRect.height) / 2;
  if (Math.abs(centred) < 1) return;

  // The browser clamps `top` to the container's scroll range, so an element
  // near either end simply lands as close to centre as the range allows.
  container.scrollTo({
    top: container.scrollTop + centred,
    behavior: prefersReducedMotion() ? 'auto' : 'smooth',
  });
}

function prefersReducedMotion(): boolean {
  return window.matchMedia?.('(prefers-reduced-motion: reduce)').matches ?? false;
}
